import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { H6 } from "@/components/ui/h6";
import { EvolveContentStrategy } from "@/api/personality";
import { useToast } from "@/hooks/use-toast";
import { Persona } from "@/api/personas.ts";
import { Textarea } from "../ui/textarea";
import { useAuth } from "@/hooks/use-auth.tsx";
import { Role } from "@/components/AuthorizationProvider.tsx";

interface ContentStrategyModalProps {
  personaId: string;
  personaData: Persona;
  authenticated: boolean;
}

export function formatContentStrategyText(text: string | undefined) {
  if (!text) return null;
  const lines = text.split("\n");
  const processedLines = lines.map((line, index) => {
    const trimmedLine = line.trim();
    if (
      /^[IVXLCDM]+\. .*$/.test(trimmedLine) ||
      /^[A-Z& ]{2,}$/.test(trimmedLine)
    ) {
      return (
        <div key={index}>
          <H6 className="font-semibold">{trimmedLine}</H6>
        </div>
      );
    } else {
      return (
        <div key={index}>
          <br />
          <p>{trimmedLine}</p>
        </div>
      );
    }
  });
  return processedLines;
}

export function ContentStrategyModal({
  personaId,
  personaData,
  authenticated,
}: ContentStrategyModalProps) {
  const [showContentStrategyModal, setShowContentStrategyModal] =
    useState(false);
  const [isEvolvingContentStrategy, setIsEvolvingContentStrategy] =
    useState(false);
  const [contentStrategyFeedbackText, setContentStrategyFeedbackText] =
    useState("");
  const [isContentStrategyEvolving, setIsContentStrategyEvolving] =
    useState(false);
  const [apiCallCompletedCS, setApiCallCompletedCS] = useState(false);
  const [stepsCompletedCS, setStepsCompletedCS] = useState(false);
  const [currentStepIndexCS, setCurrentStepIndexCS] = useState(0);
  const { toast } = useToast();
  const { hasAtLeastRole } = useAuth();
  const editPersonaDisabled =
    !personaData.persona_id ||
    (personaData.public_persona === true && !hasAtLeastRole(Role.TenantAdmin));

  const evolveStepsCS = [
    "Analyzing digital presence patterns...",
    "Mapping engagement architecture...",
    "Processing human feedback signals...",
    "Optimizing content resonance...",
    "Synthesizing evolved strategy...",
  ];

  const handleEvolveContentStrategy = async () => {
    setIsContentStrategyEvolving(true);
    setCurrentStepIndexCS(0);
    setApiCallCompletedCS(false);
    setStepsCompletedCS(false);
    try {
      await EvolveContentStrategy(personaId, contentStrategyFeedbackText);
      setApiCallCompletedCS(true);
    } catch {
      toast({ description: "Error evolving content strategy" });
      setIsContentStrategyEvolving(false);
      return;
    }
  };

  useEffect(() => {
    let interval: any;
    if (isContentStrategyEvolving) {
      setCurrentStepIndexCS(0);
      let stepIndex = 0;
      interval = setInterval(() => {
        stepIndex++;
        if (stepIndex < evolveStepsCS.length) {
          setCurrentStepIndexCS(stepIndex);
        } else {
          clearInterval(interval);
          setStepsCompletedCS(true);
        }
      }, 3500);
    }
    return () => clearInterval(interval);
  }, [isContentStrategyEvolving]);

  useEffect(() => {
    if (isContentStrategyEvolving && apiCallCompletedCS && stepsCompletedCS) {
      setIsEvolvingContentStrategy(false);
      setContentStrategyFeedbackText("");
      toast({ description: "Content strategy evolved successfully" });
      setIsContentStrategyEvolving(false);
    }
  }, [apiCallCompletedCS, stepsCompletedCS]);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowContentStrategyModal(true)}
      >
        Edit
      </Button>
      <Dialog
        open={showContentStrategyModal}
        onOpenChange={(open) => {
          setShowContentStrategyModal(open);
          if (!open) {
            setIsEvolvingContentStrategy(false);
            setContentStrategyFeedbackText("");
          }
        }}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Content Strategy</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {isContentStrategyEvolving ? (
              <div className="flex flex-col items-center">
                <LoaderCircle className="h-12 w-12 animate-spin stroke-accent m-auto" />
                <p className="mt-4 text-center">
                  {evolveStepsCS[currentStepIndexCS]}
                </p>
              </div>
            ) : !isEvolvingContentStrategy ? (
              <div className="overflow-y-auto max-h-[80vh]">
                {formatContentStrategyText(personaData.content_strategy)}
              </div>
            ) : (
              <div>
                <p>
                  Ready to elevate their content universe. Define how this agent
                  moves through digital space. Share specific content angles,
                  narrative approaches, or engagement styles that will shape
                  their presence. Direct signals only - from publishing cadence
                  to tone of voice. Your vision shapes their impact. Let's make
                  it legendary.
                </p>
                <Textarea
                  value={contentStrategyFeedbackText}
                  onChange={(e) =>
                    setContentStrategyFeedbackText(e.target.value)
                  }
                  className="w-full h-40 mt-4"
                />
              </div>
            )}
          </DialogDescription>
          <div className="flex justify-between mt-4">
            {authenticated &&
              !editPersonaDisabled &&
              !isContentStrategyEvolving && (
                <>
                  {!isEvolvingContentStrategy ? (
                    <Button onClick={() => setIsEvolvingContentStrategy(true)}>
                      Evolve Content Strategy
                    </Button>
                  ) : (
                    <Button
                      onClick={handleEvolveContentStrategy}
                      disabled={isContentStrategyEvolving}
                    >
                      {isContentStrategyEvolving
                        ? "Evolving..."
                        : `Evolve ${personaData.name}`}
                    </Button>
                  )}
                  <DialogClose asChild>
                    <Button
                      onClick={() => {
                        setShowContentStrategyModal(false);
                        setIsEvolvingContentStrategy(false);
                        setContentStrategyFeedbackText("");
                      }}
                    >
                      Close
                    </Button>
                  </DialogClose>
                </>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
